/**
 * What a session token says about itself. Every field is optional because nothing here has
 * been checked: a token older than a claim simply lacks it.
 */
export interface IJwtClaims {
    /** The account's email address. */
    sub?: string;
    /** The clinic the session is for. Absent on the platform owner's token. */
    tid?: number;
    /** When this token was issued, in seconds since the epoch. */
    iat?: number;
    /** When it stops being accepted, in seconds since the epoch. */
    exp?: number;
}

/**
 * Reads a JWT's payload. **Reads, not verifies** -- the browser has no key to verify with, so
 * this is only ever good for what the app does to itself: deciding whether a stored token is
 * worth sending, when it is due for renewal, which clinic's colours to paint. The server
 * decides everything that matters, from the same token, with the signature checked.
 *
 * Total on purpose: anything that is not a readable token is null, never an exception, since
 * the callers are render paths and interceptors.
 */
export const readJwtClaims = (token: string | null): IJwtClaims | null => {
    if (!token) {
        return null;
    }

    try {
        const payload: string | undefined = token.split('.')[1];

        if (!payload) {
            return null;
        }

        // base64url to base64, padded: `atob` accepts neither the alphabet nor a short tail.
        const base64: string = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded: string = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

        // `atob` yields one char per byte, which mangles anything outside Latin-1 -- a name
        // with a diacritic in it, say -- unless the bytes are put back together as UTF-8.
        const json: string = decodeURIComponent(
            atob(padded)
                .split('')
                .map((char: string) => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );

        const claims: unknown = JSON.parse(json);

        return typeof claims === 'object' && claims !== null ? (claims as IJwtClaims) : null;
    } catch {
        return null;
    }
};
